import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DraftToolbar — Slack-style inline formatting bar for the omnibar's
 * expanded-state Add-mode editor.
 *
 * Sits as a third row inside the expanded `<DraftBar>`, between the
 * `<RichTextEditor>` and the meta row (destination chip).
 * Only mounted when the bar carries `data-expanded` AND `data-mode=
 * "add"` — the Ask-mode textarea has no rich-text surface to format,
 * and the resting/focused-but-not-expanded states keep the bar visually
 * lightweight (the design intent for the focused-panel posture is
 * "you're committed to writing now, here's the formatting palette").
 *
 * Stays under `omnibar-host/` (rather than next to the shared
 * `<RichTextEditor>`) because the toolbar is omnibar-specific chrome:
 * the SCSS classes (`.omnibar-draft__toolbar*`) and the design intent
 * (only appears in the bar's expanded posture, never in the block-edit
 * modal) tie it to the bar. The block-edit shell could grow its own
 * formatting affordance later, but it would likely be a different
 * shape (e.g. a floating bubble menu) rather than a third row.
 *
 * Buttons (left to right):
 *
 *   • Bold   (B)
 *   • Italic (I)
 *   • Underline (U)
 *   • Strike (S)
 *   • ─ separator ─
 *   • Link
 *   • Ordered list  (1.)
 *   • Bulleted list (•)
 *
 * Reactivity:
 *
 *   The toolbar's "is this format active?" highlights need to re-evaluate
 *   on every selection change (cursor moves into / out of bold text, etc.)
 *   and on every doc edit (typing extends a mark, paste replaces marked
 *   content, etc.). Tiptap fires a `transaction` event for both — we
 *   subscribe via `editorRef.current.subscribe(listener)` (added to
 *   `RichTextEditorHandle` for exactly this case) and force a re-render
 *   through a counter-style `useReducer`. Cheaper than re-reading the
 *   editor's full state into React on every keystroke; the toolbar is
 *   a handful of small components and re-running the active-state
 *   queries against the live editor is effectively free.
 *
 *   Subscription is set up in a layout effect so the listener attaches
 *   in the same paint as the toolbar mounts — otherwise the first
 *   transaction (e.g. clicking into the editor to give it focus) would
 *   slip past us and the toolbar would render against stale active
 *   state until the next transaction arrived.
 *
 * Focus preservation:
 *
 *   Every button calls `e.preventDefault()` on mousedown so clicking a
 *   button doesn't blur the editor's contenteditable. The toggle
 *   commands themselves run through `editor.chain().focus()` (see
 *   `RichTextEditor.toggleFormat`) which re-asserts focus on the
 *   editor regardless — belt and braces.
 */
import React, { memo, useCallback, useLayoutEffect, useReducer, } from "react";
import { FaIcon } from "@shared/client/components/fa-icon/fa-icon";
import { faBold, faItalic, faUnderline, faStrikethrough, faLink, faListOl, faListUl, } from "@fortawesome/pro-solid-svg-icons";
const BUTTONS = [
    { format: "bold", icon: faBold, label: "Bold" },
    { format: "italic", icon: faItalic, label: "Italic" },
    { format: "underline", icon: faUnderline, label: "Underline" },
    { format: "strike", icon: faStrikethrough, label: "Strikethrough" },
    { format: "link", icon: faLink, label: "Link", separator: true },
    { format: "orderedList", icon: faListOl, label: "Numbered list", separator: true },
    { format: "bulletList", icon: faListUl, label: "Bulleted list" },
];
// ── Component ───────────────────────────────────────────────────────────
export const DraftToolbar = memo(({ editorRef }) => {
    // Counter-style force-render. The toolbar's render output depends on
    // `editorRef.current.isFormatActive(...)` and `hasSelection()`, both
    // of which are pulled directly from the editor each render — so all
    // we need to do on a transaction is bump the counter so React calls
    // the function body again. No state mirror, no stale-data risk.
    const [, force] = useReducer((n) => n + 1, 0);
    // Subscribe to editor transactions so the active-state highlights stay
    // in sync. `useLayoutEffect` so the subscription is in place before the
    // browser paints the first frame — prevents a flash of wrong-active
    // state if the user expands the bar with a non-empty selection that
    // happens to sit inside a marked range. `editorRef` is a stable React
    // ref so re-subscriptions won't cascade.
    useLayoutEffect(() => {
        const unsub = editorRef.current?.subscribe(force);
        return unsub;
    }, [editorRef]);
    // ── Click handlers ────────────────────────────────────────────────────
    //
    // Most buttons just call `toggleFormat(format)`. Link is special — it
    // needs a URL to apply, so it routes through the prompt-for-URL flow
    // when the cursor isn't already inside a link, and unsets cleanly
    // when it is.
    const handleToggle = useCallback((format) => {
        const editor = editorRef.current;
        if (!editor)
            return;
        if (format === "link") {
            // Active link → unset on click (no prompt). Most users expect
            // a single click to remove a link; making them confirm an empty
            // URL adds friction.
            if (editor.isFormatActive("link")) {
                editor.toggleLink(undefined);
                return;
            }
            // No selection AND no active link → nothing meaningful to mark.
            // Bail silently rather than prompting and then quietly failing
            // when the empty selection refuses the link transaction.
            if (editor.hasSelection() === false)
                return;
            // Selection present → prompt for URL. `window.prompt` is
            // intentionally minimal for v1 — the omnibar is a capture
            // surface, not a full doc editor; a richer popover with
            // validation can land later if the friction shows.
            const raw = window.prompt("Link URL", "https://");
            if (raw === null)
                return; // user cancelled
            const trimmed = raw.trim();
            if (trimmed.length === 0)
                return;
            editor.toggleLink(trimmed);
            return;
        }
        editor.toggleFormat(format);
    }, [editorRef]);
    const editor = editorRef.current;
    return (_jsx("div", { className: "omnibar-draft__toolbar", role: "toolbar", "aria-label": "Formatting", children: BUTTONS.map((btn) => {
            const isActive = editor?.isFormatActive(btn.format) === true;
            // Link button is "disabled-looking" when neither a selection
            // nor an active link is present — clicking would no-op anyway,
            // and the muted state cues the user to select text first.
            const isLinkInert = btn.format === "link" &&
                editor !== null &&
                editor !== undefined &&
                editor.isFormatActive("link") === false &&
                editor.hasSelection() === false;
            return (_jsxs(React.Fragment, { children: [btn.separator ? _jsx("span", { className: "omnibar-draft__toolbar-sep", "aria-hidden": "true" }) : null, _jsx("button", { type: "button", className: "omnibar-draft__toolbar-btn", "data-active": isActive ? "" : undefined, "data-inert": isLinkInert ? "" : undefined, "aria-label": btn.label, "aria-pressed": isActive, title: btn.label, tabIndex: -1, 
                        // Suppress focus shift on click so the editor's contenteditable
                        // keeps focus — the toggle commands re-focus internally too,
                        // but this avoids a one-frame blur visible as a caret flicker.
                        onMouseDown: (e) => e.preventDefault(), onClick: () => handleToggle(btn.format), children: _jsx(FaIcon, { icon: btn.icon }) })] }, btn.format));
        }) }));
});
